import { StepInfo } from '@voice/utils';
import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { Step, StepKey } from './onboarding.utils';
import { NgClass } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';

@Component({
  selector: 'app-client-onboarding-mini-steps',
  standalone: true,
  template: `
    @for (step of steps; track $index) {
    <div class="inline-flex align-items-center justify-content-center">
      <span
        style="position: relative"
        class="border-round w-2rem h-2rem inline-flex align-items-center justify-content-center border-2"
        [ngClass]="{
          'step-valid': step.valid === true,
          'step-invalid': step.valid === false,
          'step-partial': step.valid === 'partial',
          'surface-border': step.valid !== true && step.valid !== false && step.valid !== 'partial'
        }"
        [pTooltip]="getStepTooltip(step)"
        tooltipPosition="top"
      >
        <i [class]="step.icon"></i>
        @if (step.valid === 'partial') {
          <span
            class="step-sent-badge"
            pTooltip="Customers &amp; sites already created in Rev.io. Items pending."
            tooltipPosition="top"
          >
            <i class="pi pi-check"></i>
          </span>
        }
      </span>
      @if ($index < steps.length - 1) {
      <span class="m-1" style="color: darkgrey"> - </span>
      }
    </div>
    }
  `,
  imports: [NgClass, TooltipModule],
})
export class MiniStepsComponent implements OnInit, OnChanges {
  @Input()
  stepsInfo?: StepInfo[];

  /** Record status — SUMMARY turns green only when FINISHED */
  @Input()
  status?: string;

  /** Set when customers & sites have been sent (phase 1 done) */
  @Input()
  parentCustomerId?: number;

  steps: Step[] = [
    {
      key: StepKey.CUSTOMER,
      title: 'Customer information',
      icon: 'pi pi-user',
      valid: undefined,
    },
    {
      key: StepKey.LOCATIONS,
      title: 'Locations',
      icon: 'pi pi-map-marker',
      valid: undefined,
    },
    {
      key: StepKey.SERVICES,
      title: 'Services',
      icon: 'pi pi-cog',
      valid: undefined,
    },
    {
      key: StepKey.ITEMS,
      title: 'Items',
      icon: 'pi pi-list',
      valid: undefined,
    },
    {
      key: StepKey.SUMMARY,
      title: 'Summary',
      icon: 'pi pi-check-circle',
      valid: undefined,
    },
  ];

  getStepTooltip(step: Step): string {
    if (step.key !== StepKey.SUMMARY) return step.title;
    if (step.valid === true) return 'Completed — all data sent to Rev.io';
    if (step.valid === 'partial') return 'Step 1 of 2 done: Customers & sites created in Rev.io. Items not sent yet.';
    return step.title;
  }

  ngOnInit(): void {
    this.updateSteps();
  }

  ngOnChanges(): void {
    this.updateSteps();
  }

  private updateSteps(): void {
    if (
      !this.stepsInfo ||
      !Array.isArray(this.stepsInfo) ||
      this.stepsInfo.length === 0
    ) {
      this.steps.forEach((step) => {
        if (step.key === StepKey.CUSTOMER) {
          step.valid = true;
        } else {
          step.valid = undefined;
        }
      });
      return;
    }
    this.steps.forEach((step) => {
      const stepInfo = this.stepsInfo!.find((s) => s.step === step.key);
      if (stepInfo) {
        const rawValid =
          stepInfo.valid === true
            ? true
            : stepInfo.valid === false
            ? false
            : undefined;

        // SUMMARY: green = fully done, amber = phase 1 done (customers sent), neutral = not sent yet
        if (step.key === StepKey.SUMMARY) {
          if (this.status === 'FINISHED') {
            step.valid = true;
          } else if (this.parentCustomerId) {
            step.valid = 'partial';
          } else {
            step.valid = undefined;
          }
        } else {
          step.valid = rawValid;
        }
      } else {
        if (step.key === StepKey.CUSTOMER) {
          step.valid = true;
        } else {
          step.valid = undefined;
        }
      }
    });
  }
}
